// EligibilityRequirements.js
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, FileText, Briefcase, DollarSign, Languages, GraduationCap } from 'lucide-react';

const EligibilityRequirements = () => {
  const requirements = [
    {
      icon: Briefcase,
      title: 'Work Experience',
      description: 'Minimum 1-2 years of skilled work experience in your field (varies by program). Experience must be in NOC TEER 0, 1, 2, or 3 occupations.',
      applicable: 'Express Entry, PNPs'
    },
    {
      icon: Languages,
      title: 'Language Proficiency',
      description: 'Minimum CLB 7 (IELTS 6.0 equivalent) for Express Entry. French proficiency can provide additional points. Valid test results required (IELTS, CELPIP, or TEF).',
      applicable: 'All Programs'
    },
    {
      icon: GraduationCap,
      title: 'Education',
      description: 'Minimum Canadian high school diploma or foreign equivalent. Educational Credential Assessment (ECA) required for foreign degrees.',
      applicable: 'Express Entry, Most PNPs'
    },
    {
      icon: DollarSign,
      title: 'Proof of Funds',
      description: 'CAD $13,757 for single applicant, increasing with family size. Not required if you have a valid job offer or Canadian work experience.',
      applicable: 'Express Entry (FSWP), Some PNPs'
    },
    {
      icon: FileText,
      title: 'Valid Documents',
      description: 'Valid passport, police certificates, medical examination, and all supporting documents. Documents must be translated to English or French if in another language.',
      applicable: 'All Programs'
    },
    {
      icon: CheckCircle2,
      title: 'Admissibility',
      description: 'No criminal record, good health, and sufficient ties to support yourself in Canada. Must not be inadmissible on security, health, or financial grounds.',
      applicable: 'All Programs'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            General Eligibility Requirements
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            While specific requirements vary by program, here are the common criteria for Canadian permanent residency applications.
          </p>
        </motion.div>

        {/* Requirements Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {requirements.map((requirement, index) => {
            const Icon = requirement.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#F5F8FA] p-6 rounded-lg border border-gray-200 hover:border-[#2542D4] transition-colors"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-[#202356]" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#202356] mb-1">
                      {requirement.title}
                    </h3>
                    <span className="text-xs text-[#2542D4] font-light">
                      {requirement.applicable}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {requirement.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* CRS Score Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-gradient-to-r from-[#202356] to-[#2542D4] rounded-lg p-8 text-white"
        >
          <h3 className="text-xl font-semibold mb-4">
            Comprehensive Ranking System (CRS) Score
          </h3>
          <p className="text-white/90 text-sm font-light leading-relaxed mb-4">
            For Express Entry, your CRS score is calculated based on factors including age, education, work experience, and language proficiency. The maximum score is 1,200 points.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Core Factors</p>
              <p className="text-white/80 font-light">Up to 600 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Spouse/Partner</p>
              <p className="text-white/80 font-light">Up to 40 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Additional Factors</p>
              <p className="text-white/80 font-light">Up to 600 points</p>
            </div>
          </div>
        </motion.div>

        {/* Important Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Note:</strong> Requirements and minimum scores change regularly based on immigration targets and draw frequencies. We assess your profile against current requirements and recommend the best pathway for your situation.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default EligibilityRequirements;
